import { useState, useEffect } from 'react';
import { apiService } from '../../../components/Config/api.service';
import LoadingBarWithStyles from '../../../components/Utils/LoadingBarWithStyles';

const Recapitulatif = ({ 
  hasExcel, 
  selectedMenuName,
  totals,
  id, 
  paymentDetails, 
  setPaymentDetails,
  dateFrom,
  dateTo,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [editDetails, setEditDetails] = useState(paymentDetails || {});
  const monthOrder = ['jan', 'fev', 'mars', 'avril', 'mai', 'juin', 'juil', 'aout', 'sept', 'oct', 'nov', 'dec'];
  const showPaymentDetails =
    paymentDetails &&
    selectedMenuName == 'ETAT 1024 (1)' &&
    dateFrom &&
    dateTo &&
    (new Date(dateTo) - new Date(dateFrom)) >= 365 * 24 * 60 * 60 * 1000;

  const orderedPaymentDetails = monthOrder
  .filter(month => paymentDetails[month])
  .map(month => ({
    month,
    ...paymentDetails[month]
  }));

  useEffect(() => {
    setEditDetails(paymentDetails || {});
  }, [paymentDetails]);

  const handlePrintPDF = () => {
    if (!id) return;

    setIsLoading(true);
    apiService
      .getBlobFile(`/financial-statement/pdf/${selectedMenuName}/${id}`)
      .then((response) => {
        if (response && response.data) {
          const fileName = `Etat_${selectedMenuName}.pdf`;
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      })
      .catch((error) => {
        console.error('Erreur lors du téléchargement du PDF:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDownloadExcel = () => {
    if (!id) return;
  
    setIsLoading(true);
    apiService
      .getBlobFile(`/financial-statement/xls/${selectedMenuName} Excel/${id}`)
      .then((response) => {
        if (response && response.data) {
          const fileName = `Etat_${selectedMenuName}.xlsx`;
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      })
      .catch((error) => {
        console.error('Erreur lors du téléchargement du fichier Excel:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePaymentDetailChange = (month, field, value) => {
    setEditDetails(prev => ({
      ...prev,
      [month]: { ...prev[month], [field]: value }
    }));
  };

  const handleSavePaymentDetails = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const response = await apiService.saveElement('/update-payment-details', {
        report_id: id,
        payment_details: editDetails
      });
      // Optionally show a success message
    } catch (err) {
      // Optionally show an error message
    } finally {
      setIsLoading(false);
    }
  };
  
  function getFrenchMonth(mois) {
        const map = {
          jan: 'Janvier', fev: 'Février', mars: 'Mars', avril: 'Avril', mai: 'Mai', juin: 'Juin',
          juil: 'Juillet', aout: 'Août', sept: 'Septembre', oct: 'Octobre', nov: 'Novembre', dec: 'Décembre'
        };
        return map[mois] || mois;
      }

  function getLastDayOfMonth(month, dateFrom, dateTo) {
    // Récupère l'année depuis dateFrom ou dateTo
    const year = dateFrom ? new Date(dateFrom).getFullYear() : new Date().getFullYear();
    const monthMap = {
      jan: 0, fev: 1, mars: 2, avril: 3, mai: 4, juin: 5,
      juil: 6, aout: 7, sept: 8, oct: 9, nov: 10, dec: 11
    };
    const m = monthMap[month];
    if (m === undefined) return '';
    // Dernier jour du mois
    const lastDay = new Date(year, m + 1, 0);
    return lastDay.toISOString().slice(0, 10);
  }

  return (
    <div className="font-sans max-w-6xl mx-auto p-2">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">{selectedMenuName || 'Récapitulatif'}</h2>
          <div className="flex gap-2">
            {hasExcel && (
          <button 
            className="btn rounded-full  bg-[--color-primary] text-white hover:bg-[--bg-hover]"
            onClick={handleDownloadExcel}
          >
            Télécharger Excel
          </button>
            )}
            <button
              className="btn bg-[--bg-color] text-[--color-primary] rounded-full"
              onClick={handlePrintPDF}
            >
              Télécharger PDF
            </button>
          </div>
        </div>

      {isLoading && <LoadingBarWithStyles />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 mt-2">
        {totals &&
          Object.entries(totals).map(([key, value]) => (
            <div key={key} className="bg-[--bg-color] shadow-md rounded-lg p-6">
              <h2 className="text-3xl font-bold text-[--color-primary] mb-3">
                {value.toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}
              </h2>
              <p className="text-gray-700">{key.replace(/_/g, ' ')}</p>
            </div>
          ))}
      </div>
      {/* Payment Details */}
      {showPaymentDetails && (
        <div className="mb-6">
          <h3 className="text-xl font-bold mb-4">Détails de paiement mensuels</h3>
          <div className="overflow-x-auto rounded-lg shadow">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead className="bg-[--color-primary] text-white">
                <tr>
                  <th className="px-4 py-3 text-left rounded-tl-lg">Mois</th>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Bordereau</th>
                  <th className="px-4 py-3 text-left">IR</th>
                  <th className="px-4 py-3 text-left">TRIMF</th>
                  <th className="px-4 py-3 text-left rounded-tr-lg">CFCE</th>
                </tr>
              </thead>
              <tbody>
                {orderedPaymentDetails.map(details => {
                  const edited = editDetails[details.month] || {};
                  return (
                    <tr key={details.month}>
                      <td className="px-4 py-2 font-semibold capitalize">{getFrenchMonth(details.month)}</td>
                      <td className="px-4 py-2">
                        <input
                          type="date"
                          value={edited.date || getLastDayOfMonth(details.month, dateFrom, dateTo)}
                          onChange={e => handlePaymentDetailChange(details.month, 'date', e.target.value)}
                          className="border rounded px-3 py-2 w-full focus:ring focus:ring-[--color-primary]"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={edited.bordereau || ''}
                          onChange={e => handlePaymentDetailChange(details.month, 'bordereau', e.target.value)}
                          className="border rounded px-3 py-2 w-full focus:ring focus:ring-[--color-primary]"
                          placeholder="N° Bordereau"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={edited.ir || ''}
                          onChange={e => handlePaymentDetailChange(details.month, 'ir', e.target.value)}
                          className="border rounded px-3 py-2 w-full focus:ring focus:ring-[--color-primary]"
                          min={0}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={edited.trimf || ''}
                          onChange={e => handlePaymentDetailChange(details.month, 'trimf', e.target.value)}
                          className="border rounded px-3 py-2 w-full focus:ring focus:ring-[--color-primary]"
                          min={0}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={edited.cfce || ''}
                          onChange={e => handlePaymentDetailChange(details.month, 'cfce', e.target.value)}
                          className="border rounded px-3 py-2 w-full focus:ring focus:ring-[--color-primary]"
                          min={0}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button
            className="btn mt-6 bg-[--color-primary] text-white rounded-full px-6 py-3 shadow hover:bg-[--bg-hover] transition"
            onClick={handleSavePaymentDetails}
            disabled={isLoading}
          >
            Enregistrer les modifications
          </button>
        </div>
      )}      
    </div>
  );
};

export default Recapitulatif;